import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import pluralize from 'pluralize';
import { useFeatureFlag } from 'common/hooks';
import { getCapInformation } from 'components/ServiceAuthorization/utils';
import dollarsToCents from 'common/utils/Money/dollarsToCents';
import formatShortDate from 'common/utils/Dates/formatShortDate';
import formatSpend from 'common/utils/Money/formatSpend';
import { LineItem } from 'common/display/LineItem';
import { useAuthorizedSpendsWithExclusions } from '../hooks/useAuthorizedSpendsWithExclusions';

const filterByState = (items) => {
  const approvedItems = items.filter((item) => item.state === 'approved');
  const requestedItems = items.filter((item) => item.state === 'requested');
  return { approvedItems, requestedItems };
};

function AuthorizationSpendSection({
  feeScheduleProgram,
  person,
  isUnitBased,
  requestedStartsAt,
  requestedEndsAt,
  authorizedAmount,
  setIsAmountExceeded,
  authorization,
}) {
  const updateEditAuthorizationsEnabled = useFeatureFlag('pays-5650-update-edit-authorizations');
  const isRequestedEdition = !!authorization?.original_service_authorization?.id &&
    authorization?.state === 'requested';
  const shouldExcludeServiceAuthorizations = isRequestedEdition || authorization?.state === 'approved';
  const excludeAuthorizationId = authorization?.original_service_authorization?.id || authorization?.id;

  const exclusionFilters = updateEditAuthorizationsEnabled && shouldExcludeServiceAuthorizations ? {
    original_and_editions: { id: excludeAuthorizationId },
    non_original_from_requested: true,
  } : null;

  const baseParams = {
    feeScheduleProgram: feeScheduleProgram.id,
    person: person.id,
    requestedStartsAt: requestedStartsAt.toISOString(),
    requestedEndsAt: requestedEndsAt.toISOString(),
    enabled: requestedEndsAt &&
      requestedStartsAt &&
      requestedEndsAt.isSameOrAfter(requestedStartsAt) &&
      Boolean(feeScheduleProgram.id),
  };

  const {
    authorizedSpends,
    meta,
    isLoading,
  } = useAuthorizedSpendsWithExclusions(baseParams, exclusionFilters);

  const { approvedItems, requestedItems } = filterByState(authorizedSpends);
  const totalApprovedSpend = meta?.total_approved_spend || 0;
  const totalRequestedSpend = meta?.total_requested_spend || 0;
  const availableCap = meta?.available_cap || 0;

  const capInfo = getCapInformation(feeScheduleProgram, meta);

  const parsedAuthorizedAmount = isUnitBased ? parseInt(authorizedAmount, 10) : dollarsToCents(authorizedAmount);

  const isAmountExceeded = capInfo.hasCap && !!parsedAuthorizedAmount &&
    (parsedAuthorizedAmount > (availableCap));

  useEffect(() => {
    setIsAmountExceeded(capInfo.hasCap && isAmountExceeded);
    return () => {
      setIsAmountExceeded(false);
    };
  }, [isAmountExceeded]);

  return (
    <>
      <hr className="my-5 border-dark-fill-blue border-solid" />
      <h4 className="leading-4">
        CLIENT SERVICE UTILIZATION
      </h4>
      <p className="mt-2 mb-3">
        The following requests have service delivery dates that overlap with this cap period:
        <b> {
          capInfo.hasCap ?
            capInfo.formattedCapPeriod : `${formatShortDate(requestedStartsAt)} - ${formatShortDate(requestedEndsAt)}`
        }
        </b>
      </p>
      <dl className="grid grid-cols-2 gap-2 mt-0">
        { capInfo.hasCap && (
          <LineItem isLoaded={!isLoading} field="Available Amount">
            {capInfo.formattedAvailableAmount}
          </LineItem>
        )}
        <LineItem isLoaded={!isLoading} field="Accepted Requests">
          {updateEditAuthorizationsEnabled && isRequestedEdition && approvedItems.length > 0 ? (
            `Update pending (${approvedItems.length} ${pluralize('request', approvedItems.length)})`
          ) : (
            <>
              {formatSpend(totalApprovedSpend, feeScheduleProgram.unit, !isUnitBased)}
              {` (${approvedItems.length} ${pluralize('request', approvedItems.length)})`}
            </>
          )}
        </LineItem>
        <LineItem isLoaded={!isLoading} field="Open Requests">
          {formatSpend(totalRequestedSpend, feeScheduleProgram.unit, !isUnitBased)}
          {` (${requestedItems.length} ${pluralize('request', requestedItems.length)})`}
        </LineItem>
        { capInfo.hasCap && (
          <>
            <LineItem isLoaded={!isLoading} field="Cap">
              {capInfo.formattedCap}
            </LineItem>
            <LineItem isLoaded={!isLoading} field="Cap Period">
              {capInfo.formattedCapPeriod}
            </LineItem>
          </>
        )}
      </dl>
    </>
  );
}

AuthorizationSpendSection.propTypes = {
  feeScheduleProgram: PropTypes.object.isRequired,
  person: PropTypes.object.isRequired,
  isUnitBased: PropTypes.bool.isRequired,
  requestedStartsAt: PropTypes.object.isRequired,
  requestedEndsAt: PropTypes.object.isRequired,
  authorizedAmount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  setIsAmountExceeded: PropTypes.func.isRequired,
  authorization: PropTypes.object,
};

AuthorizationSpendSection.defaultProps = {
  authorization: null,
  authorizedAmount: null,
};

export default AuthorizationSpendSection;
